import { useCallback, useRef } from 'react'

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { styled } from 'styled-components'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  type OrchestratedListingDetails,
  type OrchestratedListingWithLotAndGroup,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  AnalyticsEntityType,
  AnalyticsSource,
  Banner,
  FormattedMessage,
  Tooltip,
} from '@b-stock/fe-core/bstock-react'
import { Colors, Typography } from '@b-stock/fe-core/design-system'

import getListingAnalyticsData from '@/buyer-portal/helpers/getListingAnalyticsData'
import type { P2SData } from '@/buyer-portal/hooks/useListingP2SData'
import { FeatureFlags } from '@/helpers/featureFlags'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

interface Props {
  p2sData: P2SData
  variant: 'text' | 'banner'
  listing?: OrchestratedListingDetails | OrchestratedListingWithLotAndGroup
}

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`

const LabelGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const LabelText = styled.span`
  ${Typography.Subtitle1}
  color: ${Colors.Semantic.Neutral.black};
`

const TooltipIcon = styled(FontAwesomeIcon)`
  color: ${Colors.Semantic.Neutral.dark_grey};
  cursor: pointer;
`

const ValueText = styled.span`
  ${Typography.Title3}
  color: ${Colors.Semantic.Neutral.very_dark_grey};
`

const StyledBanner = styled(Banner)`
  padding: 1rem 1.5rem;
  width: 100%;
`

const P2SMetrics = ({ p2sData, variant, listing }: Props) => {
  const { avgPaidToShipMs, avgPaidToShipDays, avgPaidToShipText } = p2sData
  const tooltipIconRef = useRef<HTMLSpanElement>(null)
  const analytics = useAnalytics()
  const { trackButtonClicked } = analytics
  const fmvBadgeFlag = useFlag(FeatureFlags.ENABLE_FMV_DEAL_BADGE)

  // Handle tooltip icon click tracking
  const handleTooltipClick = useCallback(() => {
    if (!listing) return

    trackButtonClicked({
      screenName: 'listing_detail_page',
      source: AnalyticsSource.HOME_PORTAL,
      entityType: AnalyticsEntityType.BUYER,
      buttonName: 'seller_time_to_ship_tooltip',
      ...getListingAnalyticsData(listing, fmvBadgeFlag, p2sData),
    })
  }, [p2sData, trackButtonClicked, listing, fmvBadgeFlag])

  if (avgPaidToShipMs === null || avgPaidToShipMs < 0) return null

  if (variant === 'banner') {
    return (
      <StyledBanner type="info">
        <FormattedMessage
          id="Listing.paidToShippedTime.bannerText"
          values={{
            days: avgPaidToShipText?.toLowerCase() ?? '',
            maxDays: avgPaidToShipDays?.max ?? 1,
            bold: (chunks) => <strong>{chunks}</strong>,
          }}
        />
      </StyledBanner>
    )
  }

  return (
    <Row data-testid="p2s-metrics">
      <LabelGroup>
        <LabelText data-testid="p2s-metrics-label">
          <FormattedMessage id="Listing.paidToShippedTime.label" />
        </LabelText>
        <span ref={tooltipIconRef} onClick={handleTooltipClick}>
          <TooltipIcon icon={faCircleInfo} />
        </span>
        {/* oxlint-disable-next-line react/refs */}
        <Tooltip referenceElement={tooltipIconRef.current}>
          <FormattedMessage id="Listing.paidToShippedTime.tooltip" />
        </Tooltip>
      </LabelGroup>
      <ValueText data-testid="p2s-metrics-value">{avgPaidToShipText}</ValueText>
    </Row>
  )
}

export default P2SMetrics
